import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInitialized = useRef(false);

  const fetchProfile = useCallback(async (userId) => {
    try {
      console.log('Fetching profile for user:', userId);
      
      // First try to get the profile
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle(); // Use maybeSingle() instead of single()

      if (error) {
        console.error('Error fetching profile:', error);
        setError(error.message);
        return;
      }

      // If no profile exists, create one
      if (!data) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError('No authenticated user found');
          return;
        }

        // Create a default profile
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert([{
            id: userId,
            first_name: user.user_metadata?.first_name || 'New',
            last_name: user.user_metadata?.last_name || 'User',
            email: user.email,
            skill_level: '2.5', // Default to lowest NTRP rating
            onboarding_completed: false
          }])
          .select()
          .single();

        if (createError) {
          console.error('Error creating profile:', createError);
          setError(createError.message);
          return;
        }

        console.log('Created new profile:', newProfile);
        setProfile(newProfile);
      } else {
        console.log('Profile retrieved successfully:', data);
        setProfile(data);
      }
    } catch (error) {
      console.error('Unexpected error in fetchProfile:', error);
      setError(error.message);
    }
  }, []);

  // Handle auth state changes
  const handleAuthStateChange = useCallback(async (event, newSession) => {
    console.log('Auth state changed:', event, newSession ? 'Session exists' : 'No session');
    
    try {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      
      if (newSession?.user) {
        // If this is a new signup (email just confirmed), create the profile
        if (event === 'SIGNED_IN') {
          const { data: existingProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', newSession.user.id)
            .single();

          if (!existingProfile) {
            // Get user metadata from the signup
            const metadata = newSession.user.user_metadata;
            
            await supabase
              .from('profiles')
              .insert([{
                id: newSession.user.id,
                first_name: metadata?.first_name || '',
                last_name: metadata?.last_name || '',
                email: newSession.user.email,
                skill_level: metadata?.skill_level || '2.5',
                phone_number: metadata?.phone_number,
                onboarding_completed: false
              }])
              .select()
              .single();
          } else {
            // Check if this is a fresh email verification (backup mechanism)
            const wasJustVerified = localStorage.getItem('email_verification_pending');
            if (wasJustVerified && newSession.user.email_confirmed_at) {
              // Send welcome email (this is a backup - main sending should happen in AuthCallback)
              console.log('AuthContext: Email verification detected, sending welcome email as backup...');
              try {
                const response = await fetch(
                  `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/quick-task`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${newSession.access_token}`,
                    },
                    body: JSON.stringify({})
                  }
                );

                const result = await response.json();
                if (response.ok && result.success) {
                  console.log('AuthContext: Welcome email sent successfully');
                } else {
                  console.error('AuthContext: Failed to send welcome email:', result.error);
                }
              } catch (emailError) {
                console.error('AuthContext: Error sending welcome email:', emailError);
              }
              
              // Clear the pending verification flag
              localStorage.removeItem('email_verification_pending');
            }
          }
        }
        
        await fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
        setError(null);
      }
    } catch (error) {
      console.error('Error in auth state change:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [fetchProfile]);

  // Initialize auth state
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    console.log('AuthProvider initializing...');
    
    let authListener = null;

    const initialize = async () => {
      try {
        // Get initial session
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error getting initial session:', sessionError);
          setError(sessionError.message);
          setLoading(false);
          return;
        }

        // Handle initial session
        await handleAuthStateChange('INITIAL_SESSION', initialSession);

        // Set up auth listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange);
        authListener = subscription;

      } catch (err) {
        console.error('Error in auth initialization:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    initialize();

    return () => {
      console.log('Cleaning up auth subscription');
      if (authListener) {
        authListener.unsubscribe();
      }
    };
  }, [handleAuthStateChange]);

  const signUp = async (email, password, firstName, lastName, skillLevel, phoneNumber) => {
    try {
      setLoading(true);
      setError(null);
      
      // Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            skill_level: skillLevel,
            phone_number: phoneNumber
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (authError) throw authError;

      // Mark that email verification is pending
      if (authData?.user && !authData.session) {
        localStorage.setItem('email_verification_pending', 'true');
      }

      // Create profile only if signup was successful
      if (authData?.user) {
        // For email confirmation flow, we won't have a session yet
        // We'll create the profile after email confirmation
        return { data: authData, error: null };
      }

      return { data: null, error: new Error('Signup failed - no user data received') };
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('AuthContext: Attempting sign in');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      console.log('AuthContext: Sign in response:', { data, error });

      if (error) {
        console.log('AuthContext: Sign in error:', error.message);
        // Check if the error is due to unconfirmed email
        if (error.message.toLowerCase().includes('email not confirmed')) {
          return { 
            data: null, 
            error: { message: 'Please verify your email before signing in.' }
          };
        }
        throw error;
      }

      // Check email verification using Supabase's built-in field
      if (!data.user.confirmed_at && !data.user.email_confirmed_at) {
        console.log('AuthContext: Email not verified');
        return { 
          data: null, 
          error: { message: 'Please verify your email before signing in.' }
        };
      }

      return { data, error: null };
    } catch (error) {
      console.error('AuthContext: Signin error:', error);
      setError(error.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any existing errors
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear local state
      setUser(null);
      setProfile(null);
      setSession(null);
    } catch (error) {
      console.error('Signout error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;
      setProfile(data);
      return { data, error: null };
    } catch (error) {
      console.error('Profile update error:', error);
      setError(error.message);
      return { data: null, error };
    }
  };

  const resetPassword = async (email) => {
    try {
      // Don't set global loading state for password reset
      // This allows the component to show success message immediately
      setError(null);
      
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      
      // Supabase always returns success for resetPasswordForEmail if no error
      // The actual success is when the email is sent
      return { data: { success: true }, error: null };
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message);
      return { data: null, error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      // Don't set global loading state for password update
      // This allows the component to handle its own loading state
      setError(null);
      
      console.log('AuthContext: Attempting to update password');
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      console.log('AuthContext: Update password response:', { data, error });

      if (error) {
        console.log('AuthContext: Password update error:', error);
        throw error;
      }
      
      console.log('AuthContext: Password updated successfully');
      return { data, error: null };
    } catch (error) {
      console.error('Password update error:', error);
      setError(error.message);
      return { data: null, error };
    }
  };

  const value = {
    user,
    profile,
    session,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    updateProfile,
    fetchProfile,
    resetPassword,
    updatePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 